// Full-history activity log drawer. Renders in a SlideOverDrawer with
// single-select filter chips (All / Status changes / Edits / Records added /
// People), day-grouped entries, and cursor-based "Load more" pagination.
// Filter state lives in the URL via ?activity_filter= so it is bookmark-safe.

import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { clsx } from "clsx";
import SlideOverDrawer from "../../../components/common/SlideOverDrawer";
import { useContractActivity } from "../../../hooks/useContractActivity";
import ActivityLogItem from "./ActivityLogItem";
import Button from "../../common/Button";
import { groupActivityByDay } from "./activityGrouping";
import type { ActivityFilter } from "../../../types";

interface ActivityLogDrawerProps {
  contractId: string;
  isOpen: boolean;
  onClose: () => void;
}

const FILTER_CHIPS: { value: ActivityFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "status_changes", label: "Status changes" },
  { value: "edits", label: "Edits" },
  { value: "records_added", label: "Records added" },
  { value: "people", label: "People" },
];

const FILTER_VALUES = new Set<string>(FILTER_CHIPS.map((c) => c.value));

export default function ActivityLogDrawer({
  contractId,
  isOpen,
  onClose,
}: ActivityLogDrawerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get("activity_filter");
  const filter: ActivityFilter = FILTER_VALUES.has(rawFilter ?? "")
    ? (rawFilter as ActivityFilter)
    : "all";

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useContractActivity(contractId, filter);

  const groups = useMemo(
    () =>
      groupActivityByDay(data?.pages.flatMap((p) => p.entries) ?? []),
    [data?.pages],
  );

  function handleFilterChange(value: ActivityFilter) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") {
      next.delete("activity_filter");
    } else {
      next.set("activity_filter", value);
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Activity log"
      width="sm"
    >
      {/* Filter chips */}
      <div
        role="group"
        aria-label="Filter activity log"
        className="mb-4 flex flex-wrap gap-2"
      >
        {FILTER_CHIPS.map((chip) => {
          const isSelected = filter === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={isSelected}
              onClick={() => handleFilterChange(chip.value)}
              className={clsx(
                "rounded-full px-3 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
                isSelected
                  ? "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600",
              )}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {/* Day-grouped list */}
      {groups.length === 0 ? (
        <p className="text-body-sm italic text-slate-500 dark:text-slate-400">
          No activity recorded yet — events will appear as the contract is
          edited.
        </p>
      ) : (
        <ol role="list" className="space-y-4">
          {groups.map((group) => (
            <li key={group.dayLabel}>
              <p className="mb-1 text-caption font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {group.dayLabel}
              </p>
              {group.entries.map((entry, ei) => (
                <ActivityLogItem
                  key={`${entry.occurredAt}#${ei}`}
                  entry={entry}
                />
              ))}
            </li>
          ))}
        </ol>
      )}

      {/* Pagination */}
      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          >
            Load more
          </Button>
        </div>
      )}
    </SlideOverDrawer>
  );
}
